/**
 * What the outbox tells the user after "Indlæs kvittering (kun admin)".
 * Every outcome gives a toast: a rejected or failed import is never silent
 * (REF02-S 2.1–2.3). Pure: no DOM, no i18n instance; the caller passes `t`.
 */
import type { OutboundReferralStatus } from './referralReceipt';

export type ReceiptImportResult = {
  importKind?: string;
  typeCode?: string;
  kind?: string;
  attachment?: 'matched' | 'mismatch' | 'doublet' | 'unreadable' | 'companion';
  originalEnvelopeIdentifier?: string | null;
  status?: OutboundReferralStatus;
  refuseText?: string | null;
};

export type ReceiptImportFeedback = {
  tone: 'success' | 'error';
  message: string;
  /** True when the row should be reloaded (the receipt was stored on it). */
  refresh: boolean;
};

type T = (key: string, fallback: string, options?: Record<string, unknown>) => string;

const PREFIX_KEY = 'patients.hospital_referral.receipt_import_rejected';
const PREFIX_FALLBACK = 'Kvitteringen blev ikke indlæst: {{reason}}';

function rejected(t: T, reason: string): ReceiptImportFeedback {
  return {
    tone: 'error',
    message: t(PREFIX_KEY, PREFIX_FALLBACK, { reason, interpolation: { escapeValue: false } }),
    refresh: false,
  };
}

export function receiptStatusLabel(t: T, status: OutboundReferralStatus): string {
  switch (status) {
    case 'xctl01':
      return t('patients.hospital_referral.receipt_badge_negative_vans', 'Negativ VANS-kvittering');
    case 'xctl02':
      return t('patients.hospital_referral.receipt_badge_negative', 'Negativ kvittering');
    case 'xctl03':
      return t('patients.hospital_referral.receipt_badge_positive', 'Positiv kvittering');
    case 'built':
      return t('patients.hospital_referral.receipt_badge_waiting', 'Afventer kvittering');
    default: {
      const unexpected: never = status;
      return unexpected;
    }
  }
}

/** Feedback for a 200 response from communicate-ctl action "import". */
export function receiptImportFeedback(
  result: ReceiptImportResult,
  row: { envelope_identifier: string },
  t: T,
  companionFileName?: string | null,
): ReceiptImportFeedback {
  if (result.importKind === 'message') {
    return rejected(
      t,
      t(
        'patients.hospital_referral.receipt_import_not_receipt',
        'Filen er en meddelelse ({{type}}), ikke en kvittering (XCTL01, XCTL02 eller XCTL03).',
        { type: result.typeCode || '?', interpolation: { escapeValue: false } },
      ),
    );
  }
  if (result.attachment === 'mismatch') {
    return rejected(
      t,
      t(
        'patients.hospital_referral.receipt_import_mismatch',
        'Kvitteringen hører ikke til denne henvisning. Den henviser til kuvert {{theirs}}, henvisningen har kuvert {{ours}}.',
        {
          theirs: result.originalEnvelopeIdentifier || '?',
          ours: row.envelope_identifier,
          interpolation: { escapeValue: false },
        },
      ),
    );
  }
  if (result.attachment === 'unreadable') {
    return rejected(
      t,
      t(
        'patients.hospital_referral.receipt_import_unreadable',
        'Filen er ikke en XCTL01-, XCTL02- eller XCTL03-kvittering.',
      ),
    );
  }
  if (result.attachment === 'companion') {
    return {
      tone: 'success',
      message: t(
        'patients.hospital_referral.receipt_import_companion',
        'Kvitteringen er indlæst på bilaget {{file}}.',
        { file: companionFileName || '', interpolation: { escapeValue: false } },
      ),
      refresh: true,
    };
  }
  if ((result.attachment === 'matched' || result.attachment === 'doublet') && result.status) {
    const label = receiptStatusLabel(t, result.status);
    return {
      tone: 'success',
      message:
        result.attachment === 'doublet'
          ? t(
              'patients.hospital_referral.receipt_import_doublet',
              'Kvitteringen var indlæst før og er indlæst igen. Status: {{status}}.',
              { status: label, interpolation: { escapeValue: false } },
            )
          : t(
              'patients.hospital_referral.receipt_import_ok',
              'Kvitteringen er indlæst. Status: {{status}}.',
              { status: label, interpolation: { escapeValue: false } },
            ),
      refresh: true,
    };
  }
  // A 200 without a known outcome (e.g. an older communicate-ctl): say so.
  return rejected(
    t,
    t(
      'patients.hospital_referral.receipt_import_unknown',
      'Serveren svarede uden at bekræfte, at kvitteringen er knyttet til henvisningen.',
    ),
  );
}

/** Feedback for a thrown error (HTTP 4xx/5xx, network). */
export function receiptImportErrorFeedback(
  err: unknown,
  t: T,
): ReceiptImportFeedback {
  const message =
    err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
      ? err.message.trim()
      : '';
  if (!message || message === 'Failed to fetch' || message === 'Load failed') {
    return rejected(
      t,
      t(
        'patients.hospital_referral.receipt_import_network',
        'Serveren kunne ikke nås. Prøv igen.',
      ),
    );
  }
  return rejected(t, message);
}
